/**
 * Reusable inline form cells for the criteria table.
 * Shared between the "add new" row and the "edit existing" row
 * to eliminate duplication.
 */
import { Spinner } from "@chakra-ui/react";
import { FiCheck, FiX } from "react-icons/fi";
import { EVALUATION_TYPE_OPTIONS } from "../../../../types/criteria.type";
import type { CriterionFormValues } from "./criteria-form.types";

interface CriterionFormCellsProps {
	formValues: CriterionFormValues;
	isSaving: boolean;
	onUpdateField: (field: keyof CriterionFormValues, value: string) => void;
	onSetFormValues: React.Dispatch<React.SetStateAction<CriterionFormValues>>;
	onSave: () => void;
	onCancel: () => void;
	/** data-testid prefix — "add" or "edit" */
	testIdPrefix: "add" | "edit";
	/** Auto-focus the name input (used for the add row) */
	autoFocusName?: boolean;
}

export const CriterionFormCells: React.FC<CriterionFormCellsProps> = ({
	formValues,
	isSaving,
	onUpdateField,
	onSetFormValues,
	onSave,
	onCancel,
	testIdPrefix,
	autoFocusName = false,
}) => (
	<>
		<td className="py-2 pr-4">
			<input
				type="text"
				value={formValues.name}
				onChange={(e) => onUpdateField("name", e.target.value)}
				placeholder={autoFocusName ? "e.g. Technical Capability" : undefined}
				className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
				autoFocus={autoFocusName}
				data-testid={`${testIdPrefix}-name-input`}
			/>
		</td>
		<td className="py-2 pr-4">
			<input
				type="text"
				value={formValues.description}
				onChange={(e) => onUpdateField("description", e.target.value)}
				placeholder={autoFocusName ? "Optional description" : undefined}
				className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
				data-testid={`${testIdPrefix}-description-input`}
			/>
		</td>
		<td className="py-2 pr-4">
			<select
				tabIndex={0}
				value={formValues.rfx_evaluation_type_id}
				onChange={(e) =>
					onSetFormValues((prev) => ({
						...prev,
						rfx_evaluation_type_id: Number(e.target.value),
					}))
				}
				className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
				data-testid={`${testIdPrefix}-type-select`}
			>
				{EVALUATION_TYPE_OPTIONS.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
		</td>
		<td className="py-2 pr-4 text-right">
			<input
				type="number"
				min="0"
				max="100"
				value={formValues.weight}
				onChange={(e) => onUpdateField("weight", e.target.value)}
				placeholder={autoFocusName ? "0" : undefined}
				className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none"
				data-testid={`${testIdPrefix}-weight-input`}
			/>
		</td>
		<td className="py-2 text-right">
			<div className="flex items-center justify-end gap-1">
				<button
					onClick={onSave}
					disabled={isSaving}
					className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-50"
					data-testid={`save-${testIdPrefix === "add" ? "new" : "edit"}-btn`}
				>
					{isSaving ? <Spinner size="xs" /> : <FiCheck size={16} />}
				</button>
				<button
					onClick={onCancel}
					className="rounded p-1 text-gray-400 hover:bg-gray-100"
					data-testid={`cancel-${testIdPrefix === "add" ? "new" : "edit"}-btn`}
				>
					<FiX size={16} />
				</button>
			</div>
		</td>
	</>
);
